/**
 * useFieldLock — live view of every soft lock on a Workbench network.
 *
 * Combines:
 *   1. A one-shot GET /networks/{id}/locks hydration (so we don't wait for
 *      the first AppSync beacon after mount).
 *   2. Real-time ``FIELD_LOCK_ACQUIRED / RELEASED / EXPIRED`` events from
 *      the Workbench channel (``events`` from ``useWorkbenchChannel``).
 *
 * Returns:
 *   - ``locksByField``   — ``{ [field]: WorkbenchLock }`` for every *live* lock
 *   - ``acquire(field)`` — acquires or refreshes the lock for the caller
 *   - ``release(field)`` — releases the caller's lock
 *   - ``forceRelease(field)`` — workspace-admin break-glass
 *   - ``getLock(field)`` — convenience accessor
 *
 * The hook is deliberately stateless about *which* input is focused; callers
 * pair it with an onFocus/onBlur effect on their own components.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  acquireFieldLock as apiAcquire,
  listFieldLocks,
  releaseFieldLock as apiRelease,
} from '../services/workbenchLocksApi';

function liveOnly(locks) {
  const now = Math.floor(Date.now() / 1000);
  const out = {};
  for (const l of locks || []) {
    if (!l || !l.field) continue;
    if (typeof l.expiresAt === 'number' && l.expiresAt <= now) continue;
    out[l.field] = l;
  }
  return out;
}

export function useFieldLock(networkId, workbenchEvents) {
  const [locksByField, setLocksByField] = useState({});
  const hydratedRef = useRef(false);
  const processedIdxRef = useRef(0);

  // Hydrate once per networkId change — the initial snapshot prevents a
  // momentary "nobody is editing" flash when a new viewer lands on a field
  // that is already locked by a peer.
  useEffect(() => {
    if (!networkId) return undefined;
    hydratedRef.current = false;
    processedIdxRef.current = 0;
    setLocksByField({});
    let cancelled = false;
    (async () => {
      try {
        const { locks } = await listFieldLocks(networkId);
        if (cancelled) return;
        setLocksByField(liveOnly(locks || []));
      } catch (err) {
        if (err?.status !== 403 && err?.status !== 404) {
          console.warn('[useFieldLock] hydrate skipped:', err?.message || err);
        }
      } finally {
        hydratedRef.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [networkId]);

  // Incremental event application — we process only the tail of the events
  // buffer so re-renders from sibling consumers don't replay history.
  useEffect(() => {
    if (!Array.isArray(workbenchEvents) || !workbenchEvents.length) return;
    const start = processedIdxRef.current;
    if (start >= workbenchEvents.length) return;
    let mutated = false;
    setLocksByField((prev) => {
      const next = { ...prev };
      for (let i = start; i < workbenchEvents.length; i += 1) {
        const evt = workbenchEvents[i] || {};
        const type = String(evt.eventType || '').toUpperCase();
        const payload = evt.payload || {};
        const field = String(payload.field || '').trim();
        if (!field) continue;
        if (type === 'FIELD_LOCK_ACQUIRED') {
          const ttl = Number(payload.ttlSeconds) || 120;
          next[field] = {
            lockId: payload.lockId,
            field,
            networkId: evt.networkId || networkId,
            holderSub: payload.holderSub,
            holderEmail: payload.holderEmail,
            acquiredAt: evt.timestamp,
            expiresAt: Math.floor(Date.now() / 1000) + ttl,
          };
          mutated = true;
        } else if (type === 'FIELD_LOCK_RELEASED' || type === 'FIELD_LOCK_EXPIRED') {
          if (next[field]) {
            delete next[field];
            mutated = true;
          }
        }
      }
      processedIdxRef.current = workbenchEvents.length;
      return mutated ? next : prev;
    });
  }, [workbenchEvents, networkId]);

  // Periodic expiry sweep — AppSync may deliver the RELEASED event late
  // (or the holder may lose network mid-edit); we don't want stale locks
  // to visually block the UI forever.
  useEffect(() => {
    const id = setInterval(() => {
      setLocksByField((prev) => {
        const now = Math.floor(Date.now() / 1000);
        let changed = false;
        const next = {};
        for (const [k, v] of Object.entries(prev)) {
          if (typeof v.expiresAt === 'number' && v.expiresAt <= now) {
            changed = true;
            continue;
          }
          next[k] = v;
        }
        return changed ? next : prev;
      });
    }, 10_000);
    return () => clearInterval(id);
  }, []);

  const acquire = useCallback(
    async (field, ttlSeconds = 120) => {
      if (!networkId || !field) return null;
      try {
        const { lock } = await apiAcquire(networkId, field, ttlSeconds);
        setLocksByField((prev) => ({ ...prev, [field]: lock }));
        return lock;
      } catch (err) {
        if (err?.status === 409 && err?.data?.lock) {
          setLocksByField((prev) => ({ ...prev, [field]: err.data.lock }));
        }
        throw err;
      }
    },
    [networkId],
  );

  const release = useCallback(
    async (field, opts = {}) => {
      if (!networkId || !field) return false;
      try {
        await apiRelease(networkId, field, opts);
        setLocksByField((prev) => {
          if (!(field in prev)) return prev;
          const next = { ...prev };
          delete next[field];
          return next;
        });
        return true;
      } catch (err) {
        console.warn('[useFieldLock] release failed:', err?.message || err);
        return false;
      }
    },
    [networkId],
  );

  const forceRelease = useCallback(
    (field) => release(field, { force: true }),
    [release],
  );

  const getLock = useCallback(
    (field) => (field ? locksByField[field] : undefined),
    [locksByField],
  );

  return useMemo(
    () => ({ locksByField, acquire, release, forceRelease, getLock }),
    [locksByField, acquire, release, forceRelease, getLock],
  );
}
